import { QueryInItInfoResponse, WebsiteInfoState } from "@/types/websiteInfo"
import { EventType } from "@common_lib/base/constants/eventTypeConst"
import { RouteRecordRaw } from "vue-router"

const modules = import.meta.glob(["../views/**/*.vue", "../components/**/*.vue"])

const renderHorseRoute = (isEnableGameHall: boolean, eventType: string) => {
  const isHorseRacing = eventType === EventType.HORSE_RACING.toString()

  return [
    {
      path: "/horse",
      name: isHorseRacing ? "Home" : "Horse",
      component: modules[`../views/mobile/horse/Horse.vue`]
    },
    {
      path: "/greyhound",
      name: isHorseRacing ? "Greyhound" : "Home",
      component: modules[`../views/mobile/greyhound/Greyhound.vue`]
    },
    {
      path: "/leagues",
      name: "Leagues",
      component: isEnableGameHall
        ? modules[`../views/mobile/highlight/Highlight.vue`]
        : modules[`../views/mobile/leagues/Leagues.vue`]
    }
  ]
}
const renderNormalRoute = (isEnableGameHall: boolean) => {
  return isEnableGameHall
    ? [
        {
          path: "/gameHall",
          name: "Home",
          component: modules[`../views/mobile/gameHall/GameHall.vue`]
        },
        {
          path: "/highlight",
          name: "Highlight",
          component: modules[`../views/mobile/highlight/Highlight.vue`]
        }
      ]
    : [
        {
          name: "Highlight",
          path: "/highlight-old",
          redirect: { name: "Home" }
        },
        {
          path: "/highlight",
          name: "Home",
          component: modules[`../views/mobile/highlight/Highlight.vue`]
        },
        {
          path: "/leagues",
          name: "Leagues",
          component: modules[`../views/mobile/leagues/Leagues.vue`]
        }
      ]
}

const renderMobileReportRoute = () => [
  {
    component: () => import(`../layouts/mobile/Default.vue`),
    path: "/mobile",
    children: [
      {
        path: "/account/profile",
        name: "Profile",
        meta: { isNeedAuth: true, isHiddenMarquee: true },
        component: modules[`../views/mobile/profile/Profile.vue`]
      },
      {
        path: "/account/accountStatement",
        name: "AccountStatement",
        meta: { isNeedAuth: true, isHiddenMarquee: true },
        component: modules[`../views/mobile/accountStatement/AccountStatement.vue`]
      },
      {
        path: "/account/transferredLog",
        name: "TransferredLog",
        meta: { isNeedAuth: true, isHiddenMarquee: true },
        component: modules[`../views/mobile/transferredLog/TransferredLog.vue`]
      },
      {
        path: "/account/activityLog",
        name: "ActivityLog",
        meta: { isNeedAuth: true, isHiddenMarquee: true },
        component: modules[`../views/mobile/activityLog/ActivityLog.vue`]
      },
      {
        path: "/account/balanceOverview",
        name: "BalanceOverview",
        meta: { isNeedAuth: true, isHiddenMarquee: true },
        component: modules[`../views/mobile/balanceOverview/BalanceOverview.vue`]
      },
      {
        path: "/account/myBets",
        name: "MyBets",
        meta: { isNeedAuth: true, isHiddenMarquee: true },
        component: modules[`../views/mobile/myBets/MyBets.vue`],
        children: [
          {
            path: "/account/myBets/currentBets",
            name: "CurrentBets",
            component: modules[`../views/mobile/myBets/CurrentBets.vue`]
          },
          {
            path: "/account/myBets/betsHistory",
            name: "BetsHistory",
            component: modules[`../views/mobile/myBets/BetsHistory.vue`]
          },
          {
            path: "/account/myBets/profitLoss",
            name: "ProfitLoss",
            component: modules[`../views/mobile/myBets/ProfitLoss.vue`]
          }
        ]
      }
    ]
  }
]

const renderDesktopReportRoute = () => [
  {
    path: "/desktop",
    component: () => import(`../layouts/desktop/Default.vue`),
    meta: { isNeedAuth: true },
    children: [
      {
        path: "/desktop/account",
        meta: { layout: "TwoPaneLayout", gap: 15 },
        components: {
          "left-group": modules[`../components/desktop/menu/Account.vue`],
          "right-group": () => import(`../layouts/desktop/Account.vue`)
        },
        children: [
          {
            path: "/desktop/account/profile",
            name: "Profile",
            component: modules[`../views/desktop/profile/Profile.vue`]
          },
          {
            path: "/desktop/account/accountStatement",
            name: "AccountStatement",
            component: modules[`../views/desktop/accountStatement/AccountStatement.vue`]
          },
          {
            path: "/desktop/account/transferredLog",
            name: "TransferredLog",
            component: modules[`../views/desktop/transferredLog/TransferredLog.vue`]
          },
          {
            path: "/desktop/account/activityLog",
            name: "ActivityLog",
            component: modules[`../views/desktop/activityLog/ActivityLog.vue`]
          },
          {
            path: "/desktop/account/balanceOverview",
            name: "BalanceOverview",
            component: modules[`../views/desktop/balanceOverview/BalanceOverview.vue`]
          },
          {
            path: "/desktop/account/myBets",
            name: "MyBets",
            component: modules[`../views/desktop/myBets/MyBets.vue`],
            children: [
              {
                path: "/desktop/account/myBets/currentBets",
                name: "CurrentBets",
                component: modules[`../views/desktop/myBets/CurrentBets.vue`]
              },
              {
                path: "/desktop/account/myBets/betsHistory",
                name: "BetsHistory",
                component: modules[`../views/desktop/myBets/BetsHistory.vue`]
              },
              {
                path: "/desktop/account/myBets/profitLoss",
                name: "ProfitLoss",
                component: modules[`../views/desktop/myBets/ProfitLoss.vue`]
              }
            ]
          }
        ]
      }
    ]
  }
]
export const renderRoutes = (
  data: QueryInItInfoResponse &
    Pick<WebsiteInfoState, "isEnableMobileReportPage"> & { eventType?: string }
): RouteRecordRaw[] => {
  const { isRacingWebSite, isEnableGameHall, eventType, isEnableMobileReportPage } = data

  const websiteRoutes = isRacingWebSite
    ? renderHorseRoute(isEnableGameHall, eventType ?? "7")
    : renderNormalRoute(isEnableGameHall)

  const reportsRoutes = isEnableMobileReportPage
    ? renderMobileReportRoute()
    : renderDesktopReportRoute()

  return [
    ...reportsRoutes,
    {
      path: "/",
      component: () => import(`../layouts/mobile/Default.vue`),
      redirect: () => {
        return { name: "Home" }
      },
      children: [
        ...websiteRoutes,
        {
          path: "/fullMarket/:eventType-:eventId",
          name: "FullMarket",
          meta: { isHiddenMarquee: true },
          component: modules[`../views/mobile/fullMarket/FullMarket.vue`]
        },
        {
          path: "/multiMarket",
          name: "MultiMarket",
          component: modules[`../views/mobile/multiMarket/MultiMarket.vue`]
        },
        {
          path: "/inPlay",
          name: "In-Play",
          component: modules[`../views/mobile/inPlay/InPlay.vue`]
        },
        {
          path: "/inPlay/result",
          name: "In-Play-Result",
          meta: { isNeedAuth: true },
          component: modules[`../views/mobile/inPlay/Result.vue`]
        },
        {
          path: "/account",
          name: "Account",
          meta: { isNeedAuth: true },
          component: modules[`../views/mobile/account/Account.vue`]
        }
      ]
    },

    {
      path: "/approval",
      component: () => import("../layouts/mobile/Empty.vue"),
      children: [
        {
          path: "/terms",
          name: "Terms",
          meta: { isNeedAuth: true },
          component: modules[`../views/mobile/approval/Terms.vue`]
        },
        {
          path: "/resetPassword",
          name: "ResetPassword",
          meta: { isNeedAuth: true },
          component: modules[`../views/mobile/approval/ResetPassword.vue`]
        },
        {
          path: "/login",
          name: "Login",
          component: modules[`../views/mobile/login/Login.vue`]
        }
      ]
    },
    {
      path: "/error",
      component: () => import("../layouts/mobile/Empty.vue"),
      children: [
        {
          path: "/IPBlock",
          name: "IPBlock",
          component: modules[`../views/mobile/pageError/IPBlock.vue`]
        },
        {
          path: "/SystemMaintenance",
          name: "SystemMaintenance",
          component: modules[`../views/mobile/pageError/SystemMaintenance.vue`]
        },
        {
          path: "/Horizontal",
          name: "Horizontal",
          component: modules["../views/mobile/pageError/Horizontal.vue"]
        },
        {
          path: "/AuthBlock",
          name: "AuthBlock",
          component: modules["../views/mobile/pageError/AuthBlock.vue"]
        }
      ]
    },
    {
      path: "/error-default",
      component: () => import(`../layouts/mobile/Default.vue`),
      children: [
        {
          path: "/404",
          name: "404",
          component: modules[`../views/mobile/pageError/404.vue`]
        }
      ]
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: () => ({ name: "404", params: {} })
    }
  ]
}

export default {}
